import * as THREE from 'three';
import { LineSegments2 } from 'three/examples/jsm/lines/LineSegments2.js';
import { LineSegmentsGeometry } from 'three/examples/jsm/lines/LineSegmentsGeometry.js';
import { LineMaterial } from 'three/examples/jsm/lines/LineMaterial.js';
import { Visualizer } from '../visualizer.js';
import { safeZoneUniforms } from '../shaders/safeZone.glsl.js';
import { getHersheySans, preloadHersheySans } from '../hershey-font.js';

// Shared-rig contract: forbidden. A blueprint does not emit and is not lit.
const RIG = 'forbidden';
export { RIG };

export const PROFILE = {
  id: 'blueprint',
  family: 'vector',
  danceability: 0.40,
  energyLevel: 0.60,
  bpmMin: 95,
  bpmMax: 160,
  movement: 0.55,
  tonality: 0.50,
  prefers: { gridStrength: [0.55, 1.0], density: [0.0, 0.70] },
  enabled: true
};

// Blueprint: mechanical assemblies DRAWN by a plotter on cyanotype paper,
// line by line, then dimensioned, turned, exploded and put back together.
//
// THE OBJECT IS NOT RENDERED, IT IS DRAWN. Every part is a small closed mesh
// (extrusions of 2D profiles: a rounded box, discs, toothed wheels, hex
// bolts, a link) and what reaches the screen is its EDGES: creases (faces
// meeting at more than 30 degrees) and silhouettes (a front face against a
// back face, which is what a cylinder's outline is). Each edge is then
// split into short samples and every sample is tested against the front
// faces of every part (a screen grid of triangles, then a barycentric depth
// test): the visible runs are drawn in a strong continuous line, the hidden
// ones in a medium DASHED line - the convention of technical drawing, and
// what makes the object read as an object. That classification is redone
// whenever the pose changes (a 7.5-degree notch on every beat of ROTATE, the
// explosion and its breathing), so as the assembly turns the hidden edges
// change state under the eye.
//
// EVERY BEAT DOES SOMETHING, whatever the phase: TRACE draws a part, COTE a
// cote, ROTATE turns a notch and puts one part in accent for the beat (and
// on the downbeat sweeps a cut plane A-A across the assembly, hatching the
// faces it cuts in accent), EXPLODE and HOLD breathe, ERASE takes a part off.
//
// THE PEN. What the classification produces is a list of 2D strokes per
// part, ordered contours first and hidden edges after. A part's progress
// (0..1) walks that list at constant speed: whole strokes up to the pen,
// the one under the pen cut at the pen, and an accent dot where the pen is.
// One part per beat. The strokes carry a hand's imperfection - a bend of
// half a pixel in the middle, a pixel of overshoot at the ends.
//
// THREE INKS AND A PAPER. Off-white, the accent (the cover's own colour at
// saturation 0.8, for whatever is being drawn now) and the paper - a dark
// cyanotype in the cover's own hue, Prussian blue without a cover - never flat: grain, marbling, two folds, a dog-eared
// corner, the lamp at the top left. No bloom: a drawing does not emit.

// --- inks (DISPLAY values, converted to linear below; no tone mapping) --------
const PAPER_RGB = [0.05, 0.12, 0.30];
const INK_RGB = [0.92, 0.94, 0.98];
const INK_PEAK = 0.85;                 // luminance ceiling on the strokes
const ACCENT_SAT = 0.8, ACCENT_LIGHT = 0.58;
const PAPER_LIGHT = 0.175;            // the sheet's lightness, whatever its hue (Prussian blue's)
const PAPER_MIN_CHROMA = 0.18;        // a dominant colour under this saturation does not give its hue
const FALLBACK_ACCENT = [0.90, 0.22, 0.16];
const PAPER_HUE = 0.62;               // Prussian blue, when there is no cover
const ACCENT_MIN_HUE_GAP = 0.14;      // an accent this close to the paper's hue does not read
const APP_NAME = 'HALOFLOOR';
const NOTES = ['ALL DIMENSIONS IN MM', 'DO NOT SCALE DRAWING', 'THIRD ANGLE PROJECTION'];
const NOTES_H = 0.30;                 // of the frame's half height, kept for the notes and the ruler
const CARTOUCHE_LIFT = 0.12;          // the title block's lower edge, of the frame's height
const SMALL_FILL = 0.8;               // a small view fills its frame to 80%: 10% of margin each side
const SECTION_HATCH_PX = 5;
const BREATH = 0.04;                  // the exploded view breathes +4% on the beat (x bands.low)

// --- strokes ---------------------------------------------------------------------
const W_STRONG = 2.5, W_MEDIUM = 1.5, W_FINE = 0.8;    // px
const AA_PX = 1.0;
const DASH_PX = 6, GAP_PX = 4;
const MIXED = [16, 3, 3, 3];          // long, gap, short, gap (trait mixte)
const WOBBLE_PX = 0.5, WOBBLE_HIGH = 0.3;
const OVERSHOOT_PX = 1;
const CREASE_COS = Math.cos(30 * Math.PI / 180);
const PEN_PX = 6;

// --- the cycle ---------------------------------------------------------------------
const TRACE_OF_BEAT = 0.85;            // a part is drawn in 85% of its beat
const COTE_MIN_BEATS = 4;            // COTE: one cote per beat, at least four beats
const ROTATE_PHRASES = 1;
const EXPLODE_BEATS = 2, ASSEMBLE_BEATS = 2;   // ERASE: one part per beat
const NOTCH_DEG = 7.5, NOTCH_S = 0.15;          // ROTATE: a notch on every beat
const DROP_OUT = 2.2;                  // the drop throws the parts this far past their exploded place
const DROP_BACK_BARS = 2;
const DROP_LEAD_BEATS = 4;
const BEATS_PER_BAR = 4, BEATS_PER_PHRASE = 16;
const SYNTH_BPM = 124;
const SCALE_MM = 50;                   // object unit -> the numbers written on the drawing

const SAFE_FLOOR = 0.6;
const TIER = {
  0: { views: 1, hidden: false, cotes: false, section: false, cartouche: false, balloons: false },
  1: { views: 1, hidden: true, cotes: true, section: false, cartouche: false, balloons: false },
  2: { views: 4, hidden: true, cotes: true, section: true, cartouche: true, balloons: true }
};
const DEFAULT_TIER = 2;
const PHASES = ['trace', 'cote', 'rotate', 'explode', 'hold', 'assemble', 'erase'];

function clamp01(x) { return Math.max(0, Math.min(1, x)); }
function lerp(a, b, t) { return a + (b - a) * t; }
function smooth(t) { t = clamp01(t); return t * t * (3 - 2 * t); }
function urlParam(name) {
  if (typeof location === 'undefined') return null;
  return new URLSearchParams(location.search).get(name);
}
function srgbToLinear(c) { return c <= 0.04045 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4); }
function lin3(c) { return [srgbToLinear(c[0]), srgbToLinear(c[1]), srgbToLinear(c[2])]; }
function lcg(seed) { let s = seed >>> 0 || 1; return function () { s = (s * 1664525 + 1013904223) >>> 0; return s / 4294967296; }; }
function hashf(i) { const s = Math.sin(i * 12.9898 + 78.233) * 43758.5453; return s - Math.floor(s); }

function hslToRgb(h, s, l) {
  const k = (n) => (n + h * 12) % 12;
  const a = s * Math.min(l, 1 - l);
  const f = (n) => l - a * Math.max(-1, Math.min(k(n) - 3, Math.min(9 - k(n), 1)));
  return [f(0), f(8), f(4)];
}
function rgbToHsl(r, g, b) {
  const mx = Math.max(r, g, b), mn = Math.min(r, g, b), l = (mx + mn) / 2;
  if (mx === mn) return [0, 0, l];
  const d = mx - mn, s = l > 0.5 ? d / (2 - mx - mn) : d / (mx + mn);
  let h;
  if (mx === r) h = (g - b) / d + (g < b ? 6 : 0); else if (mx === g) h = (b - r) / d + 2; else h = (r - g) / d + 4;
  return [h / 6, s, l];
}

// =============================================================================
// GEOMETRY: extruded profiles with edge adjacency
// =============================================================================

// A 2D profile (points [y, z], star-shaped about `centre`) extruded along x
// from x0 to x1: a closed mesh with outward triangles, face normals and the
// edge list with the two faces each edge borders.
export function extrude(profile, x0, x1, centre) {
  let pts = profile.slice();
  let area = 0;
  for (let i = 0; i < pts.length; i++) { const a = pts[i], b = pts[(i + 1) % pts.length]; area += a[0] * b[1] - b[0] * a[1]; }
  if (area < 0) pts.reverse();
  const n = pts.length;
  let c = centre;
  if (!c) { c = [0, 0]; for (const p of pts) { c[0] += p[0] / n; c[1] += p[1] / n; } }
  const V = 2 * n + 2;
  const pos = new Float32Array(V * 3);
  for (let i = 0; i < n; i++) {
    pos.set([x0, pts[i][0], pts[i][1]], i * 3);
    pos.set([x1, pts[i][0], pts[i][1]], (n + i) * 3);
  }
  pos.set([x0, c[0], c[1]], 2 * n * 3);
  pos.set([x1, c[0], c[1]], (2 * n + 1) * 3);
  const tris = [], want = [];
  for (let i = 0; i < n; i++) {
    const j = (i + 1) % n;
    const dy = pts[j][0] - pts[i][0], dz = pts[j][1] - pts[i][1];
    const nn = [0, dz, -dy];
    tris.push([i, j, n + j]); want.push(nn);
    tris.push([i, n + j, n + i]); want.push(nn);
    tris.push([2 * n, j, i]); want.push([-1, 0, 0]);
    tris.push([2 * n + 1, n + i, n + j]); want.push([1, 0, 0]);
  }
  return finishMesh(pos, tris, want);
}

function finishMesh(pos, tris, want) {
  const T = tris.length;
  const idx = new Uint32Array(T * 3), nrm = new Float32Array(T * 3);
  for (let t = 0; t < T; t++) {
    let [a, b, c] = tris[t];
    const ax = pos[a * 3], ay = pos[a * 3 + 1], az = pos[a * 3 + 2];
    const ux = pos[b * 3] - ax, uy = pos[b * 3 + 1] - ay, uz = pos[b * 3 + 2] - az;
    const vx = pos[c * 3] - ax, vy = pos[c * 3 + 1] - ay, vz = pos[c * 3 + 2] - az;
    let nx = uy * vz - uz * vy, ny = uz * vx - ux * vz, nz = ux * vy - uy * vx;
    const w = want[t];
    if (nx * w[0] + ny * w[1] + nz * w[2] < 0) { const s = b; b = c; c = s; nx = -nx; ny = -ny; nz = -nz; }
    const l = Math.hypot(nx, ny, nz) || 1;
    idx[t * 3] = a; idx[t * 3 + 1] = b; idx[t * 3 + 2] = c;
    nrm[t * 3] = nx / l; nrm[t * 3 + 1] = ny / l; nrm[t * 3 + 2] = nz / l;
  }
  const map = new Map();
  for (let t = 0; t < T; t++) for (let k = 0; k < 3; k++) {
    const a = idx[t * 3 + k], b = idx[t * 3 + (k + 1) % 3];
    const key = a < b ? a * 100000 + b : b * 100000 + a;
    const e = map.get(key);
    if (e) e.push(t); else map.set(key, [Math.min(a, b), Math.max(a, b), t]);
  }
  const edges = [];
  for (const e of map.values()) {
    const f0 = e[2], f1 = e.length > 3 ? e[3] : -1;
    let crease = 1;
    if (f1 >= 0) {
      const d = nrm[f0 * 3] * nrm[f1 * 3] + nrm[f0 * 3 + 1] * nrm[f1 * 3 + 1] + nrm[f0 * 3 + 2] * nrm[f1 * 3 + 2];
      crease = d < CREASE_COS ? 1 : 0;
    }
    edges.push(e[0], e[1], f0, f1, crease);
  }
  return { pos, tris: idx, nrm, edges: Int32Array.from(edges), V: pos.length / 3, T };
}

function circle(r, n, cy, cz) { const out = []; for (let i = 0; i < n; i++) { const a = i / n * Math.PI * 2; out.push([(cy || 0) + r * Math.cos(a), (cz || 0) + r * Math.sin(a)]); } return out; }
function roundedRect(w, h, rad, seg) {
  const out = [];
  const cs = [[w / 2 - rad, h / 2 - rad, 0], [-w / 2 + rad, h / 2 - rad, 1], [-w / 2 + rad, -h / 2 + rad, 2], [w / 2 - rad, -h / 2 + rad, 3]];
  for (const [cx, cy, q] of cs) for (let k = 0; k <= seg; k++) { const a = (q + k / seg) * Math.PI / 2; out.push([cx + rad * Math.cos(a), cy + rad * Math.sin(a)]); }
  return out;
}
function gearProfile(rRoot, rTip, teeth) {
  const out = [];
  const step = Math.PI * 2 / teeth;
  for (let i = 0; i < teeth; i++) {
    const a = i * step;
    out.push([rRoot * Math.cos(a), rRoot * Math.sin(a)]);
    out.push([rTip * Math.cos(a + step * 0.18), rTip * Math.sin(a + step * 0.18)]);
    out.push([rTip * Math.cos(a + step * 0.42), rTip * Math.sin(a + step * 0.42)]);
    out.push([rRoot * Math.cos(a + step * 0.6), rRoot * Math.sin(a + step * 0.6)]);
  }
  return out;
}
function hull(points) {
  const p = points.slice().sort((a, b) => a[0] - b[0] || a[1] - b[1]);
  const cross = (o, a, b) => (a[0] - o[0]) * (b[1] - o[1]) - (a[1] - o[1]) * (b[0] - o[0]);
  const lo = [], up = [];
  for (const q of p) { while (lo.length >= 2 && cross(lo[lo.length - 2], lo[lo.length - 1], q) <= 0) lo.pop(); lo.push(q); }
  for (let i = p.length - 1; i >= 0; i--) { const q = p[i]; while (up.length >= 2 && cross(up[up.length - 2], up[up.length - 1], q) <= 0) up.pop(); up.push(q); }
  up.pop(); lo.pop();
  return lo.concat(up);
}

// A mechanical assembly of n parts (6..12) along the x axis. Every part:
// { kind, meshes, lines (polylines with no faces), x0, x1, r, len, dia,
//   side (-1 | 0 | 1 for the explosion), dist (exploded offset) }.
export function buildAssembly(n, seed) {
  const rng = lcg(seed || 1);
  n = Math.max(6, Math.min(12, Math.round(n)));
  const parts = [];
  const pick = (a) => a[Math.floor(rng() * a.length)];
  // the ones every assembly has: the housing in the middle, the shaft through all
  const others = n - 2;
  const pool = ['bearing', 'gear', 'bearing', 'gear', 'collar', 'spring', 'gear', 'bearing', 'collar', 'gear'];
  const kinds = [];
  for (let i = 0; i < others; i++) kinds.push(pool[i % pool.length]);
  // the ends: a flange on one, a link on the other (when there is room)
  if (others >= 4) { kinds[others - 1] = 'flange'; kinds[others - 2] = 'conrod'; }
  for (let i = kinds.length - 1; i > 0; i--) { const j = Math.floor(rng() * (i - 1)); if (kinds[i] !== 'flange' && kinds[i] !== 'conrod' && kinds[j] !== 'flange' && kinds[j] !== 'conrod') { const t = kinds[i]; kinds[i] = kinds[j]; kinds[j] = t; } }
  const WIDTH = { bearing: 0.18, gear: 0.12, collar: 0.10, spring: 0.42, flange: 0.08, conrod: 0.07 };
  const GAP = 0.05;
  const carterLen = 0.62;
  // alternate sides: left, right, left...; the flange and the link at the far ends
  const left = [], right = [];
  kinds.forEach((k, i) => { (i % 2 === 0 ? left : right).push(k); });
  const place = (list, dir) => {
    let x = dir * (carterLen / 2 + GAP);
    list.forEach((kind, j) => {
      const w = WIDTH[kind];
      const x0 = dir > 0 ? x : x - w, x1 = dir > 0 ? x + w : x;
      x += dir * (w + GAP);
      parts.push(makePart(kind, x0, x1, rng, dir, j));
    });
    return Math.abs(x);
  };
  const reachL = place(left, -1), reachR = place(right, 1);
  // the housing
  const carter = { kind: 'carter', meshes: [extrude(roundedRect(0.92, 0.78, 0.14, 4), -carterLen / 2, carterLen / 2)], lines: [], x0: -carterLen / 2, x1: carterLen / 2, r: 0.60, len: carterLen, dia: 0, side: 0, dist: 0, profileW: 0.92, profileH: 0.78 };
  parts.unshift(carter);
  // the shaft, through everything and a little past
  const sx0 = -reachL - 0.06, sx1 = reachR + 0.06;
  parts.unshift({ kind: 'shaft', meshes: [extrude(circle(0.075, 16), sx0, sx1)], lines: [], x0: sx0, x1: sx1, r: 0.075, len: sx1 - sx0, dia: 0.15, side: 0, dist: 0 });
  // centre the assembly on x
  const mid = (sx0 + sx1) / 2;
  for (const p of parts) {
    for (const m of p.meshes) for (let i = 0; i < m.V; i++) m.pos[i * 3] -= mid;
    for (const l of p.lines) for (const q of l) q[0] -= mid;
    p.x0 -= mid; p.x1 -= mid;
  }
  // the explosion's distances: further out, further away
  for (const p of parts) if (p.side) p.dist = 0.22 + 0.55 * Math.abs((p.x0 + p.x1) / 2) / Math.max(reachL, reachR);
  // the drawing order: shaft, housing, then outward alternating
  const order = parts.map((p, i) => i).sort((a, b) => {
    const pa = parts[a], pb = parts[b];
    const ka = pa.kind === 'shaft' ? -2 : pa.kind === 'carter' ? -1 : Math.abs((pa.x0 + pa.x1) / 2);
    const kb = pb.kind === 'shaft' ? -2 : pb.kind === 'carter' ? -1 : Math.abs((pb.x0 + pb.x1) / 2);
    return ka - kb;
  });
  let R = 0;
  for (const p of parts) for (const m of p.meshes) for (let i = 0; i < m.V; i++) R = Math.max(R, Math.hypot(m.pos[i * 3], m.pos[i * 3 + 1], m.pos[i * 3 + 2]));
  // balloon numbers follow the axis, left to right
  const byX = parts.map((p, i) => i).sort((a, b) => (parts[a].x0 + parts[a].x1) - (parts[b].x0 + parts[b].x1));
  byX.forEach((i, k) => { parts[i].repere = k + 1; });
  return { parts, order, R, halfLen: Math.max(Math.abs(sx0 - mid), Math.abs(sx1 - mid)) };
}

function makePart(kind, x0, x1, rng, dir, j) {
  const len = x1 - x0;
  const base = { kind, x0, x1, len, lines: [], side: dir, dist: 0, dia: 0 };
  if (kind === 'bearing') {
    const r = 0.22 + 0.06 * rng();
    return Object.assign(base, { meshes: [extrude(circle(r, 24), x0, x1)], r, dia: 2 * r });
  }
  if (kind === 'collar') {
    const r = 0.14 + 0.03 * rng();
    return Object.assign(base, { meshes: [extrude(circle(r, 18), x0, x1)], r, dia: 2 * r });
  }
  if (kind === 'gear') {
    const rTip = 0.40 + 0.16 * rng(), rRoot = rTip - 0.06;
    const teeth = 12 + 2 * Math.floor(rng() * 4);
    return Object.assign(base, { meshes: [extrude(gearProfile(rRoot, rTip, teeth), x0, x1, [0, 0]), extrude(circle(0.13, 16), x0 - 0.03, x1 + 0.03)], r: rTip, dia: 2 * rTip, teeth });
  }
  if (kind === 'flange') {
    const r = 0.44 + 0.08 * rng();
    const meshes = [extrude(circle(r, 28), x0, x1)];
    const bolts = 6;
    const bx0 = dir > 0 ? x1 : x0 - 0.05, bx1 = dir > 0 ? x1 + 0.05 : x0;
    for (let k = 0; k < bolts; k++) {
      const a = k / bolts * Math.PI * 2 + Math.PI / bolts;
      const cy = 0.78 * r * Math.cos(a), cz = 0.78 * r * Math.sin(a);
      meshes.push(extrude(circle(0.045, 6, cy, cz), bx0, bx1, [cy, cz]));
    }
    return Object.assign(base, { meshes, r, dia: 2 * r });
  }
  if (kind === 'spring') {
    const r = 0.15, turns = 5, per = 16;
    const pts = [];
    for (let i = 0; i <= turns * per; i++) { const t = i / (turns * per); const a = t * turns * Math.PI * 2; pts.push([x0 + t * len, r * Math.cos(a), r * Math.sin(a)]); }
    return Object.assign(base, { meshes: [], lines: [pts], r, dia: 2 * r });
  }
  // conrod: a link lying across the axis, big eye on the shaft, small eye below
  const big = 0.19, small = 0.10, arm = 0.62;
  const prof = hull(circle(big, 20).concat(circle(small, 16, -arm, 0)));
  return Object.assign(base, { meshes: [extrude(prof, x0, x1)], r: arm + small, dia: 0, arm });
}

// =============================================================================
// VIEWS: projection and hidden-line classification
// =============================================================================

function matMul(a, b) { const o = new Array(9).fill(0); for (let r = 0; r < 3; r++) for (let c = 0; c < 3; c++) for (let k = 0; k < 3; k++) o[r * 3 + c] += a[r * 3 + k] * b[k * 3 + c]; return o; }
function rotX(t) { const c = Math.cos(t), s = Math.sin(t); return [1, 0, 0, 0, c, -s, 0, s, c]; }
function rotY(t) { const c = Math.cos(t), s = Math.sin(t); return [c, 0, s, 0, 1, 0, -s, 0, c]; }
export const VIEW_BASIS = {
  iso: matMul(rotX(35.264 * Math.PI / 180), rotY(-45 * Math.PI / 180)),
  front: [1, 0, 0, 0, 1, 0, 0, 0, 1],
  top: rotX(Math.PI / 2),
  side: rotY(-Math.PI / 2)      // the end view: the axis toward the viewer
};

// Classifies every drawable edge of the assembly in one view. pose: { yaw,
// dx[] (per-part offset along the axis), spin[] (per-part turn about it) }.
// Returns, per part, the strokes: { x0, y0, x1, y1, hidden } in view units
// (the caller scales and places them), in the order contours first.
export function classifyView(asm, basis, pose, opts) {
  const o = opts || {};
  const hiddenOn = o.hidden !== false;
  const pxPerUnit = o.pxPerUnit || 200;
  const M = matMul(basis, rotY(pose.yaw || 0));
  const parts = asm.parts;
  // 1. project every vertex, and every face's facing
  let totalT = 0;
  const projs = [];
  for (let k = 0; k < parts.length; k++) {
    const p = parts[k];
    const dx = pose.dx ? pose.dx[k] : 0, sp = pose.spin ? pose.spin[k] : 0;
    const cs = Math.cos(sp), sn = Math.sin(sp);
    const tf = (x, y, z, out, o3) => {
      const y2 = cs * y - sn * z, z2 = sn * y + cs * z, x2 = x + dx;
      out[o3] = M[0] * x2 + M[1] * y2 + M[2] * z2;
      out[o3 + 1] = M[3] * x2 + M[4] * y2 + M[5] * z2;
      out[o3 + 2] = -(M[6] * x2 + M[7] * y2 + M[8] * z2);   // depth: smaller is nearer
    };
    const meshes = [];
    for (const m of p.meshes) {
      const P = new Float32Array(m.V * 3);
      for (let i = 0; i < m.V; i++) tf(m.pos[i * 3], m.pos[i * 3 + 1], m.pos[i * 3 + 2], P, i * 3);
      const front = new Uint8Array(m.T);
      for (let t = 0; t < m.T; t++) {
        const nx = m.nrm[t * 3], ny = m.nrm[t * 3 + 1], nz = m.nrm[t * 3 + 2];
        const ny2 = cs * ny - sn * nz, nz2 = sn * ny + cs * nz;
        const vz = M[6] * nx + M[7] * ny2 + M[8] * nz2;
        front[t] = vz > 1e-6 ? 1 : 0;
      }
      meshes.push({ m, P, front });
      totalT += m.T;
    }
    const lines = p.lines.map((l) => { const P = new Float32Array(l.length * 3); l.forEach((q, i) => tf(q[0], q[1], q[2], P, i * 3)); return P; });
    projs.push({ meshes, lines });
  }
  // 2. the front triangles in a screen grid
  let bx0 = 1e9, by0 = 1e9, bx1 = -1e9, by1 = -1e9;
  for (const pr of projs) for (const mm of pr.meshes) for (let i = 0; i < mm.m.V; i++) { const x = mm.P[i * 3], y = mm.P[i * 3 + 1]; if (x < bx0) bx0 = x; if (x > bx1) bx1 = x; if (y < by0) by0 = y; if (y > by1) by1 = y; }
  const G = 24;
  const gw = Math.max(1e-6, (bx1 - bx0) / G), gh = Math.max(1e-6, (by1 - by0) / G);
  const tri = [];
  let meshId = 0;
  const meshIds = [];
  for (const pr of projs) for (const mm of pr.meshes) {
    const id = meshId++; meshIds.push(mm); mm.id = id;
    if (!hiddenOn) continue;
    for (let t = 0; t < mm.m.T; t++) {
      if (!mm.front[t]) continue;
      const a = mm.m.tris[t * 3], b = mm.m.tris[t * 3 + 1], c = mm.m.tris[t * 3 + 2];
      tri.push(id, t, mm.P[a * 3], mm.P[a * 3 + 1], mm.P[a * 3 + 2], mm.P[b * 3], mm.P[b * 3 + 1], mm.P[b * 3 + 2], mm.P[c * 3], mm.P[c * 3 + 1], mm.P[c * 3 + 2]);
    }
  }
  const NT = tri.length / 11;
  const cells = new Array(G * G);
  for (let i = 0; i < G * G; i++) cells[i] = [];
  for (let i = 0; i < NT; i++) {
    const o3 = i * 11;
    const xs = [tri[o3 + 2], tri[o3 + 5], tri[o3 + 8]], ys = [tri[o3 + 3], tri[o3 + 6], tri[o3 + 9]];
    const c0 = Math.max(0, Math.floor((Math.min(...xs) - bx0) / gw)), c1 = Math.min(G - 1, Math.floor((Math.max(...xs) - bx0) / gw));
    const r0 = Math.max(0, Math.floor((Math.min(...ys) - by0) / gh)), r1 = Math.min(G - 1, Math.floor((Math.max(...ys) - by0) / gh));
    for (let r = r0; r <= r1; r++) for (let c = c0; c <= c1; c++) cells[r * G + c].push(i);
  }
  const eps = 0.003 * asm.R;
  const hiddenAt = (x, y, d, id, f0, f1) => {
    const c = Math.floor((x - bx0) / gw), r = Math.floor((y - by0) / gh);
    if (c < 0 || r < 0 || c >= G || r >= G) return false;
    const list = cells[r * G + c];
    for (let k = 0; k < list.length; k++) {
      const o3 = list[k] * 11;
      if (tri[o3] === id && (tri[o3 + 1] === f0 || tri[o3 + 1] === f1)) continue;
      const ax = tri[o3 + 2], ay = tri[o3 + 3], az = tri[o3 + 4];
      const bx = tri[o3 + 5], by = tri[o3 + 6], bz = tri[o3 + 7];
      const cx = tri[o3 + 8], cy = tri[o3 + 9], cz = tri[o3 + 10];
      const den = (by - cy) * (ax - cx) + (cx - bx) * (ay - cy);
      if (Math.abs(den) < 1e-12) continue;
      const l1 = ((by - cy) * (x - cx) + (cx - bx) * (y - cy)) / den;
      const l2 = ((cy - ay) * (x - cx) + (ax - cx) * (y - cy)) / den;
      const l3 = 1 - l1 - l2;
      if (l1 < 0 || l2 < 0 || l3 < 0) continue;
      const dt = l1 * az + l2 * bz + l3 * cz;
      if (dt < d - eps) return true;
    }
    return false;
  };
  // 3. the edges: creases and silhouettes, sampled, merged into runs
  const out = [];
  const pushEdge = (strokes, ax, ay, az, bx, by, bz, id, f0, f1, forceVisible) => {
    const lenPx = Math.hypot(bx - ax, by - ay) * pxPerUnit;
    const K = Math.max(1, Math.min(32, Math.ceil(lenPx / 5)));
    let runStart = 0, runState = null;
    for (let s = 0; s <= K; s++) {
      let st = null;
      if (s < K) {
        const t = (s + 0.5) / K;
        st = forceVisible || !hiddenOn ? false : hiddenAt(ax + (bx - ax) * t, ay + (by - ay) * t, az + (bz - az) * t, id, f0, f1);
      }
      if (s === K || st !== runState) {
        if (runState !== null) {
          const t0 = runStart / K, t1 = s / K;
          strokes.push({ x0: ax + (bx - ax) * t0, y0: ay + (by - ay) * t0, x1: ax + (bx - ax) * t1, y1: ay + (by - ay) * t1, hidden: runState });
        }
        runStart = s; runState = st;
      }
    }
  };
  for (let k = 0; k < parts.length; k++) {
    const strokes = [];
    for (const mm of projs[k].meshes) {
      const E = mm.m.edges;
      for (let e = 0; e < E.length; e += 5) {
        const a = E[e], b = E[e + 1], f0 = E[e + 2], f1 = E[e + 3], crease = E[e + 4];
        const fr0 = mm.front[f0], fr1 = f1 >= 0 ? mm.front[f1] : 0;
        const sil = f1 >= 0 && fr0 !== fr1;
        if (!crease && !sil) continue;
        if (!hiddenOn && !(fr0 || fr1)) continue;     // tier 0: back edges are simply not drawn
        pushEdge(strokes, mm.P[a * 3], mm.P[a * 3 + 1], mm.P[a * 3 + 2], mm.P[b * 3], mm.P[b * 3 + 1], mm.P[b * 3 + 2], mm.id, f0, f1, false);
      }
    }
    for (const L of projs[k].lines) {
      for (let i = 0; i + 1 < L.length / 3; i++) pushEdge(strokes, L[i * 3], L[i * 3 + 1], L[i * 3 + 2], L[i * 3 + 3], L[i * 3 + 4], L[i * 3 + 5], -1, -1, -1, false);
    }
    // contours first, then the hidden edges: that is the order the pen draws
    strokes.sort((a, b) => (a.hidden ? 1 : 0) - (b.hidden ? 1 : 0));
    out.push(strokes);
  }
  return { strokes: out, bbox: [bx0, by0, bx1, by1], M, projs };
}

// =============================================================================
// SHADERS: the paper
// =============================================================================

const PAPER_VERTEX = `
  precision mediump float;
  varying vec2 vWorld;
  void main() {
    vWorld = position.xy;
    gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
  }
`;
// The paper. The first version's grain hashed floor(world / px) / 997 through
// fract(): a LINEAR function of the pixel position, so the "grain" was a
// field of 45-degree stripes over the whole sheet. Now a real per-pixel hash
// (Dave Hoskins' hash12) on gl_FragCoord, in highp.
const PAPER_FRAGMENT = `
  precision highp float;
  uniform vec3 uPaper;         // linear
  uniform vec2 uHalf;          // half width, half height of the frame
  uniform float uPx;           // one pixel, world
  varying vec2 vWorld;
  float hash12(vec2 p) {
    vec3 p3 = fract(vec3(p.xyx) * 0.1031);
    p3 += dot(p3, p3.yzx + 33.33);
    return fract((p3.x + p3.y) * p3.z);
  }
  float vnoise(vec2 p) {
    vec2 i = floor(p), f = fract(p);
    f = f * f * (3.0 - 2.0 * f);
    float a = hash12(i), b = hash12(i + vec2(1.0, 0.0));
    float c = hash12(i + vec2(0.0, 1.0)), d = hash12(i + vec2(1.0, 1.0));
    return mix(mix(a, b, f.x), mix(c, d, f.x), f.y);
  }
  void main() {
    vec2 uv = vWorld / (2.0 * uHalf) + 0.5;           // 0..1 over the frame
    vec2 q = vWorld / uHalf.y;                        // height units
    vec3 c = uPaper;
    // marbling: the uneven take of the cyanotype, light, two slow octaves
    float m = vnoise(q * 1.7 + 7.0) * 0.6 + vnoise(q * 4.1 + 3.0) * 0.4;
    c *= 0.84 + 0.32 * m;
    // fine grain, per pixel
    c *= 0.92 + 0.16 * hash12(floor(gl_FragCoord.xy));
    // the lamp at the top left
    c *= 1.0 + 0.45 * exp(-dot(uv - vec2(0.0, 1.0), uv - vec2(0.0, 1.0)) * 2.0);
    // the folds: a third across, half-way down - a paler crease with a shade on one side
    float fx = (uv.x - 1.0 / 3.0) * 2.0 * uHalf.x / uPx;
    float fy = (uv.y - 0.5) * 2.0 * uHalf.y / uPx;
    c *= 1.0 + 0.55 * exp(-fx * fx / 4.0) - 0.18 * exp(-(fx - 5.0) * (fx - 5.0) / 18.0);
    c *= 1.0 + 0.55 * exp(-fy * fy / 4.0) - 0.18 * exp(-(fy + 5.0) * (fy + 5.0) / 18.0);
    // the dog-eared corner, top right: the back of the sheet shows, paler, with its shadow
    vec2 cr = vec2(1.0 - uv.x, 1.0 - uv.y) * 2.0 * uHalf / uHalf.y;
    float k = cr.x + cr.y;
    if (k < 0.16) c = mix(c, uPaper * 2.4 + vec3(0.02), 0.85);
    else c *= 1.0 - 0.35 * exp(-(k - 0.16) * (k - 0.16) / 0.0004);
    gl_FragColor = vec4(c, 1.0);
  }
`;
const PEN_VERTEX = `
  precision mediump float;
  uniform float uSize;
  void main() {
    gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
    gl_PointSize = uSize;
  }
`;
const PEN_FRAGMENT = `
  precision mediump float;
  uniform vec3 uColor;
  void main() {
    float r = length(gl_PointCoord - 0.5) * 2.0;
    float a = 1.0 - smoothstep(0.6, 1.0, r);
    if (a <= 0.0) discard;
    gl_FragColor = vec4(uColor, a);
  }
`;

// =============================================================================
// THE VISUALIZER
// =============================================================================

export class BlueprintVisualizer extends Visualizer {
  constructor(options) {
    super();
    const opts = options || {};
    let tier = opts.qualityTier != null ? opts.qualityTier : DEFAULT_TIER;
    if (!TIER[tier]) tier = 0;
    this._tier = tier;
    this._layout = TIER[tier];

    this._time = 0;
    this._paused = false;
    this._beatClock = 0; this._lastBeat = null; this._lastBarIndex = null; this._lastPhraseIndex = null; this._synthBeat = 0;
    this._bpm = SYNTH_BPM;
    this._phase = 'trace'; this._phaseBeat = 0; this._phrasesLeft = 0;
    this._seed = 1;
    this._progress = 0;
    this._yaw = 0.35; this._yawFrom = 0.35; this._yawTo = 0.35; this._glideT = 1e9; this._glideS = NOTCH_S;
    this._lowBand = 0.5; this._highBand = 0.5; this._live = false;
    this._accent = lin3(FALLBACK_ACCENT);
    this._accentDisplay = FALLBACK_ACCENT.slice();
    this._ink = lin3(INK_RGB.map((v) => v * INK_PEAK / 0.94));
    this._title = ''; this._artist = '';
    this._staticFrom = 0;
    this._dropLead = false; this._lastDropTime = -1e9; this._dropFrom = 0;
    this._retraceFrom = null;
    // the beat's own events in ROTATE: the part under inspection, the cut plane
    this._inspect = -1; this._inspectBeat = -1e9; this._inspectN = 0;
    this._sweepFrom = null; this._lastBarBeat = -1e9;
    // the cotes and annotations, drawn one per beat in COTE
    this._items = [];
    this._itemStage = { kind: 'none', t: 0, v: 0 };
    this._dirty = true;
    this._font = null;
    this._firstFrame = true;

    const fp = urlParam('bpphase');
    this._forcedPhase = PHASES.indexOf(fp) >= 0 ? fp : null;
    this._debugTrace = urlParam('bptrace') === '1';
    this._debugExplode = urlParam('bpexplode') === '1';
    this._debugDone = false;
    this._postProcessing = { bloomStrength: 0, vignette: 0.12, aberration: 0, grain: 0.012, toneMapping: THREE.NoToneMapping, toneMappingExposure: 1 };
  }

  get postProcessing() { return this._postProcessing; }

  init(scene, camera) {
    this._scene = scene;
    this._camera = camera;
    camera.position.set(0, 0, 10);
    camera.lookAt(0, 0, 0);
    camera.updateProjectionMatrix();
    const HH = 10 * Math.tan(camera.fov * Math.PI / 360);
    this._HH = HH;
    this._aspect = safeZoneUniforms.uAspect.value || 1.78;
    this._HW = HH * this._aspect;
    this._bufH = 720; this._bufW = 720 * this._aspect;
    this._px = 2 * HH / this._bufH;
    if (!getHersheySans()) preloadHersheySans();

    // the paper
    this._paperUniforms = { uPaper: { value: new THREE.Vector3() }, uHalf: { value: new THREE.Vector2(this._HW, HH) }, uPx: { value: this._px } };
    this._paper = new THREE.Mesh(new THREE.PlaneGeometry(this._HW * 2.2, HH * 2.2), new THREE.ShaderMaterial({ uniforms: this._paperUniforms, vertexShader: PAPER_VERTEX, fragmentShader: PAPER_FRAGMENT, depthTest: false, depthWrite: false }));
    this._paper.frustumCulled = false;
    this._paper.renderOrder = -10;
    const self = this;
    this._paper.onBeforeRender = function (renderer) { self._onFrame(renderer); };
    scene.add(this._paper);

    // three line weights
    this._lines = [W_STRONG, W_MEDIUM, W_FINE].map((w, i) => this._makeLines(w, [10000, 10000, 16000][i], 1 + i));
    // the pens
    const penGeo = new THREE.BufferGeometry();
    this._penPos = new Float32Array(8 * 3);
    penGeo.setAttribute('position', new THREE.BufferAttribute(this._penPos, 3).setUsage(THREE.DynamicDrawUsage));
    penGeo.setDrawRange(0, 0);
    this._penUniforms = { uSize: { value: PEN_PX }, uColor: { value: new THREE.Vector3(...this._accent) } };
    this._pens = new THREE.Points(penGeo, new THREE.ShaderMaterial({ uniforms: this._penUniforms, vertexShader: PEN_VERTEX, fragmentShader: PEN_FRAGMENT, transparent: true, depthTest: false, depthWrite: false }));
    this._pens.frustumCulled = false;
    this._pens.renderOrder = 10;
    scene.add(this._pens);

    this._layoutViews();
    this._newAssembly();
    this._setPaper();
    this._enter(this._forcedPhase || 'trace', 0);
  }

  _makeLines(widthPx, capacity, order) {
    const mat = new LineMaterial({
      linewidth: widthPx + 2 * AA_PX, vertexColors: true, transparent: true,
      depthTest: false, depthWrite: false, dashed: true, dashSize: 1e9, gapSize: 0, alphaToCoverage: false
    });
    mat.resolution.set(1280, 720);
    // the analytic edge (Signal's): the quad is drawn AA_PX wider each side
    // and the alpha ramps to zero over that band, so even a 0.8 px line never
    // falls under two pixels of quad, where fat lines break up
    const halfPx = { value: (widthPx + 2 * AA_PX) / 2 };
    mat.onBeforeCompile = (shader) => {
      shader.uniforms.uHalfPx = halfPx;
      shader.fragmentShader = 'uniform float uHalfPx;\n' + shader.fragmentShader.replace(
        'gl_FragColor = vec4( diffuseColor.rgb, alpha );',
        'alpha *= clamp((1.0 - abs(vUv.x)) * uHalfPx / ' + AA_PX.toFixed(2) + ', 0.0, 1.0);\n\t\t\tgl_FragColor = vec4( diffuseColor.rgb, alpha );');
    };
    mat.customProgramCacheKey = () => 'blueprint-aa';
    const geo = new LineSegmentsGeometry();
    geo.setPositions(new Float32Array(capacity * 6));
    geo.setColors(new Float32Array(capacity * 6));
    geo.instanceCount = 0;
    const mesh = new LineSegments2(geo, mat);
    mesh.frustumCulled = false;
    mesh.renderOrder = order;
    this._scene.add(mesh);
    const pos = geo.attributes.instanceStart.data, col = geo.attributes.instanceColorStart.data;
    pos.setUsage(THREE.DynamicDrawUsage); col.setUsage(THREE.DynamicDrawUsage);
    return { mesh, geo, mat, pos, col, cap: capacity, n: 0 };
  }

  _onFrame(renderer) {
    renderer.getDrawingBufferSize(_size);
    if (_size.y !== this._bufH || _size.x !== this._bufW) { this._bufH = _size.y; this._bufW = _size.x; this._px = 2 * this._HH / Math.max(1, _size.y); this._paperUniforms.uPx.value = this._px; this._dirty = true; }
    for (const L of this._lines) L.mat.resolution.set(_size.x, _size.y);
    this._penUniforms.uSize.value = PEN_PX * Math.max(1, _size.y / 720);
  }

  // --- layout -----------------------------------------------------------------
  // The isometric view, 12% of the width to the left; the three small views
  // (FRONT, TOP, SIDE) framed and stacked down the left edge, above the notes.
  _layoutViews() {
    const HH = this._HH, HW = this._HW;
    this._aspect = safeZoneUniforms.uAspect.value || this._aspect;
    const m = 0.05 * HH;
    const views = [{ name: 'iso', basis: VIEW_BASIS.iso, cx: -0.12 * 2 * HW, cy: 0.06 * HH, fit: Math.min(0.80 * HH, 0.36 * 2 * HW), main: true }];
    if (this._layout.views >= 4) {
      const top = HH - m, bottom = -HH + NOTES_H * HH;
      const bh = (top - bottom - 2 * m) / 3, bw = Math.min(0.66 * HH, 0.2 * 2 * HW);
      [['FRONT', VIEW_BASIS.front], ['TOP', VIEW_BASIS.top], ['SIDE', VIEW_BASIS.side]].forEach(([label, basis], i) => {
        const y1 = top - i * (bh + m), y0 = y1 - bh;
        views.push({ name: label.toLowerCase(), label, basis, cx: -HW + m + bw / 2, cy: (y0 + y1) / 2, box: [-HW + m, y0, -HW + m + bw, y1] });
      });
      // the main view never crosses the column of small views (an exploded
      // flange did): it is clipped at the column's edge
      views[0].clip = [-HW + 1.5 * m + bw, -HH, HW, HH];
    }
    this._views = views;
    this._dirty = true;
  }

  _newAssembly() {
    const p = this._progress;
    const n = 6 + Math.round(6 * (1 - Math.abs(2 * p - 1)));
    this._seed = (this._seed * 7919 + 17) % 100003;
    this._asm = buildAssembly(n, this._seed);
    const N = this._asm.parts.length;
    this._partP = new Float32Array(N);
    this._axisP = 0;
    this._dx = new Float32Array(N);
    this._spin = new Float32Array(N);
    this._items = [];
    this._plan = 'PR-' + String(1000 + Math.floor(hashf(this._seed) * 8999));
    this._newAssemblyScale();
  }

  // The main view is scaled for the EXPLODED assembly (the flange of an
  // exploded view otherwise runs into the small views). Each small view is
  // fitted to its frame with 10% of margin for EVERY yaw: under a turn about
  // the vertical, a view's horizontal extent never exceeds the assembly's
  // reach in the horizontal plane, and its vertical extent is the parts'
  // height - or, from the top, that same horizontal reach. Fitted to the
  // exploded reach the assembled drawing was a third of its frame; the
  // explosion and the drop are clipped to the frame instead.
  _newAssemblyScale() {
    const parts = this._asm.parts;
    const maxDist = Math.max(0, ...parts.map((p) => p.dist || 0));
    let exz = 0, ry = 0;
    for (const p of parts) {
      const d = 0;       // the ASSEMBLED reach: an exploded view is clipped to the frame instead
      for (const m of p.meshes) for (let i = 0; i < m.V; i++) {
        exz = Math.max(exz, Math.hypot(Math.abs(m.pos[i * 3]) + d, m.pos[i * 3 + 2]));
        ry = Math.max(ry, Math.abs(m.pos[i * 3 + 1]));
      }
      for (const l of p.lines) for (const q of l) { exz = Math.max(exz, Math.hypot(Math.abs(q[0]) + d, q[2])); ry = Math.max(ry, Math.abs(q[1])); }
    }
    for (const v of this._views) {
      if (v.main) { v.scale = v.fit / Math.max(this._asm.R, 1.1 * (this._asm.halfLen + maxDist)); continue; }
      const bw = v.box[2] - v.box[0], bh = v.box[3] - v.box[1];
      const hy = v.name === 'top' ? exz : ry;
      v.scale = Math.min(SMALL_FILL * bw / 2 / exz, SMALL_FILL * bh / 2 / hy);
    }
    this._dirty = true;
  }

  _setPaper() {
    this._paperUniforms.uPaper.value.set(...lin3(this._paperDisplay || PAPER_RGB));
    this._penUniforms.uColor.value.set(...this._accent);
  }

  // THE PAPER COMES FROM THE COVER. Its hue is the cover's most frequent
  // chromatic colour (the first dominant colour with some saturation), at the
  // lightness of Prussian blue (0.17) and a saturation held between 0.25 and
  // 0.72: a cyanotype in the cover's colour, always dark enough for the white
  // ink. A colourless cover gives a slate-grey sheet; no cover, the Prussian
  // blue. The accent then has to read on THAT paper: the first vivid colour at
  // least 0.14 of hue away from it, else the paper's complement (red on a
  // colourless sheet).
  _setAccent(palette) {
    const toHsl = (c) => rgbToHsl(((c >> 16) & 255) / 255, ((c >> 8) & 255) / 255, (c & 255) / 255);
    const dom = palette && palette.dominantColors && palette.dominantColors.length ? palette.dominantColors : [];
    let paperHue = PAPER_HUE, paperSat = 0.71, chromatic = true;
    if (dom.length) {
      const hsl = dom.map(toHsl);
      const pick = hsl.find((c) => c[1] >= PAPER_MIN_CHROMA);
      if (pick) { paperHue = pick[0]; paperSat = Math.min(0.72, Math.max(0.25, pick[1])); }
      else { paperHue = hsl[0][0]; paperSat = 0.12; chromatic = false; }
    }
    this._paperHue = paperHue;
    this._paperDisplay = hslToRgb(paperHue, paperSat, PAPER_LIGHT);
    const vivid = palette && palette.vividColors && palette.vividColors.length ? palette.vividColors : [];
    let rgb = chromatic ? hslToRgb((paperHue + 0.5) % 1, ACCENT_SAT, ACCENT_LIGHT) : FALLBACK_ACCENT;
    for (const c of vivid) {
      const [h, s2] = toHsl(c);
      let dh = Math.abs(h - paperHue); dh = Math.min(dh, 1 - dh);
      if (dh >= ACCENT_MIN_HUE_GAP && s2 > 0.15) { rgb = hslToRgb(h, ACCENT_SAT, ACCENT_LIGHT); break; }
    }
    this._accentDisplay = rgb;
    this._accent = lin3(rgb);
    if (this._paperUniforms) this._setPaper();
  }

  // --- the phases --------------------------------------------------------------
  _enter(phase, beat) {
    this._phase = phase;
    this._phaseBeat = beat;
    if (phase === 'trace') { this._partP.fill(0); this._axisP = 0; this._dx.fill(0); this._spin.fill(0); this._itemStage = { kind: 'none', t: 0, v: 0 }; }
    else this._partP.fill(1);
    if (phase === 'cote') this._itemStage = { kind: 'seq', t: 0, v: 0 };
    if (phase === 'rotate') { this._itemStage = { kind: 'all', t: 0, v: 1 }; this._phrasesLeft = ROTATE_PHRASES; this._inspectN = 0; }
    if (phase === 'hold') { this._axisP = 1; this._itemStage = { kind: 'none', t: 0, v: 0 }; }
    if (phase === 'explode') this._axisP = 0;
    this._sweepFrom = null;
    this._dirty = true;
  }

  _advanceClock(dt, profile) {
    const hasGrid = profile.beatIndex != null && profile.beatPhase != null;
    let beatClock;
    if (hasGrid) {
      beatClock = profile.beatIndex + profile.beatPhase;
      // A seek (or the capture harness's looping clock) moves the grid under
      // the drawing. Every phase mark is a beat number, so they all move by
      // the same jump: the pen carries on where it was.
      const jump = beatClock - this._beatClock;
      if (this._lastBeat != null && (jump > 4 || jump < -0.5)) {
        this._phaseBeat += jump; this._staticFrom += jump; this._dropFrom += jump;
        this._inspectBeat += Math.round(jump); this._lastBarBeat += Math.round(jump);
        if (this._sweepFrom != null) this._sweepFrom += jump;
        if (this._retraceFrom != null) this._retraceFrom += jump;
        this._lastBeat = Math.floor(beatClock); this._lastBarIndex = profile.barIndex; this._lastPhraseIndex = profile.phraseIndex;
      }
    } else {
      this._synthBeat += dt * this._bpm / 60;
      beatClock = this._synthBeat;
    }
    this._beatClock = beatClock;
    const beat = Math.floor(beatClock);
    if (this._lastBeat == null) {
      this._lastBeat = beat;
      if (this._phaseBeat === 0) { this._phaseBeat = Math.ceil(beatClock); this._staticFrom = this._phaseBeat; }
      return;
    }
    while (this._lastBeat < beat) {
      this._lastBeat++;
      let isBar = false, isPhrase = false;
      if (hasGrid) {
        const bar = profile.barIndex;
        if (bar != null && bar !== this._lastBarIndex) { isBar = this._lastBarIndex != null; this._lastBarIndex = bar; }
        const phrase = profile.phraseIndex;
        if (phrase != null && phrase !== this._lastPhraseIndex) { isPhrase = this._lastPhraseIndex != null; this._lastPhraseIndex = phrase; }
      } else {
        isBar = this._lastBeat % BEATS_PER_BAR === 0;
        isPhrase = this._lastBeat % BEATS_PER_PHRASE === 0;
      }
      this._onBeat(isBar);
      if (isPhrase) this._onPhrase();
    }
  }

  // Every beat, whatever the phase, changes something on the sheet. Here the
  // ones that are events: in ROTATE, a 7.5-degree notch, the next part in
  // accent for the beat, and on the downbeat the cut plane's sweep.
  _onBeat(isBar) {
    const n = this._lastBeat;
    this._dirty = true;                      // the tremble is re-dealt too
    if (isBar) this._lastBarBeat = n;
    if (this._phase === 'rotate') {
      this._yawFrom = this._yaw;
      this._yawTo += NOTCH_DEG * Math.PI / 180;
      this._glideT = 0; this._glideS = NOTCH_S;
      const order = this._asm.order;
      this._inspect = order[this._inspectN++ % order.length];
      this._inspectBeat = n;
      if (isBar) this._sweepFrom = n;
    }
  }
  _onPhrase() {
    if (this._forcedPhase) return;
    if (this._phase === 'rotate') { this._phrasesLeft--; if (this._phrasesLeft <= 0) this._enter('explode', this._lastBeat); }
    else if (this._phase === 'hold') this._enter('assemble', this._lastBeat);
  }

  _startDrop() {
    const N = this._partP.length;
    this._partP.fill(1);
    this._phase = 'drop';
    this._dropFrom = this._beatClock;
    this._sweepFrom = null;
    this._spinDir = new Float32Array(N);
    for (let k = 0; k < N; k++) this._spinDir[k] = hashf(k + this._seed) < 0.5 ? -1 : 1;
  }

  // --- the frame ------------------------------------------------------------------
  update(deltaTime, profile) {
    const dt = Math.min(deltaTime, 0.1);
    this._paused = profile.playbackState === 'PAUSED';
    if (this._paused) return;
    this._time += dt;

    const palette = profile.artworkPalette;
    if (palette !== this._lastPalette) { this._lastPalette = palette; this._setAccent(palette); }
    const title = profile.title || '', artist = profile.artist || '';
    if (title !== this._title || artist !== this._artist) { this._title = title; this._artist = artist; this._staticFrom = Math.ceil(this._beatClock); this._dirty = true; }
    this._progress = clamp01(profile.progress || 0);
    // init() ran before the track's progress was known: the first assembly's
    // size (6 parts at the ends of a track, 12 in the middle) is decided here
    if (this._firstFrame) { this._firstFrame = false; this._newAssembly(); this._enter(this._forcedPhase || 'trace', 0); }
    this._bpm = (profile.trackFeatures && profile.trackFeatures.bpm) || SYNTH_BPM;
    const director = profile.director || {};
    this._live = !!(director.hasReliableGrid && director.bands);
    if (this._live) { this._lowBand = clamp01(director.bands.low); this._highBand = clamp01(director.bands.high); }
    const aspect = safeZoneUniforms.uAspect.value || 0;
    if (aspect && Math.abs(aspect - this._aspect) > 1e-3) { this._HW = this._HH * aspect; this._paperUniforms.uHalf.value.set(this._HW, this._HH); this._layoutViews(); this._newAssemblyScale(); }
    this._advanceClock(dt, profile);
    const b = this._beatClock;

    if (!this._debugDone && this._time > 1.0) {
      if (this._debugTrace) { this._debugDone = true; this._newAssembly(); this._enter('trace', Math.ceil(b)); }
      if (this._debugExplode) { this._debugDone = true; this._startDrop(); }
    }

    // the drop and its four beats of warning
    const ev = director.nextEvent;
    this._dropLead = false;
    if (ev && ev.type === 'drop' && ev.msUntil != null) {
      const lead = DROP_LEAD_BEATS * 60 / this._bpm * 1000;
      if (ev.msUntil > 0 && ev.msUntil <= lead) this._dropLead = true;
      if (ev.msUntil <= 0 && this._time - this._lastDropTime > 2 && this._phase !== 'drop') { this._lastDropTime = this._time; this._startDrop(); }
    }

    this._advancePhase(b);

    // the rotation notch, interpolated over 150 ms
    this._glideT += dt;
    const yaw = lerp(this._yawFrom, this._yawTo, smooth(this._glideT / this._glideS));
    if (yaw !== this._yaw) { this._yaw = yaw; this._dirty = true; }

    if (this._dirty) { this._classify(); this._dirty = false; }
    this._emit(b);
  }

  _advancePhase(b) {
    const N = this._partP.length, order = this._asm.order, parts = this._asm.parts;
    const t = b - this._phaseBeat;
    const hold = !!this._forcedPhase;
    // the explosion breathes on the beat: +4% (x bands.low) and back
    const f = b - Math.floor(b);
    const pulse = f < 0.12 ? f / 0.12 : Math.exp(-(f - 0.12) * 4);
    const breath = 1 + BREATH * (0.5 + this._lowBand) * pulse;
    const setDx = (amount, breathe) => {
      const k2 = breathe ? breath : 1;
      for (let k = 0; k < N; k++) { const v = parts[k].side * parts[k].dist * amount * k2; if (v !== this._dx[k]) { this._dx[k] = v; this._dirty = true; } }
    };
    switch (this._phase) {
      case 'trace': {
        // one part per beat
        for (let j = 0; j < N; j++) this._partP[order[j]] = clamp01((t - j) / TRACE_OF_BEAT);
        if (t >= N && !hold) this._enter('cote', this._phaseBeat + N);
        break;
      }
      case 'cote': {
        // one cote (or annotation) per beat
        this._itemStage = { kind: 'seq', t: hold ? 99 : t, v: 0 };
        const len = Math.max(COTE_MIN_BEATS, this._items.length);
        if (t >= len && !hold) this._enter('rotate', this._phaseBeat + len);
        break;
      }
      case 'rotate': {
        if (this._retraceFrom != null) {
          const r = clamp01((b - this._retraceFrom) / TRACE_OF_BEAT);
          this._itemStage = { kind: 'all', t: 0, v: r };
          if (r >= 1) this._retraceFrom = null;
        }
        break;
      }
      case 'explode': {
        const s = hold ? EXPLODE_BEATS : t;
        setDx(smooth(s / EXPLODE_BEATS), true);
        this._itemStage = { kind: 'all', t: 0, v: 1 - clamp01(s / TRACE_OF_BEAT) };
        this._axisP = clamp01(s / EXPLODE_BEATS);
        if (t >= EXPLODE_BEATS && !hold) this._enter('hold', this._phaseBeat + EXPLODE_BEATS);
        break;
      }
      case 'hold': this._axisP = 1; setDx(1, true); break;
      case 'assemble': {
        setDx(1 - smooth(t / ASSEMBLE_BEATS), false);
        this._axisP = 1 - clamp01(t / ASSEMBLE_BEATS);
        if (t >= ASSEMBLE_BEATS && !hold) this._enter('erase', this._phaseBeat + ASSEMBLE_BEATS);
        break;
      }
      case 'erase': {
        // one part erased per beat, in the reverse of the drawing order
        const s = hold ? N * 0.5 : t;
        for (let j = 0; j < N; j++) this._partP[order[N - 1 - j]] = 1 - clamp01((s - j) / TRACE_OF_BEAT);
        if (t >= N && !hold) { this._newAssembly(); this._enter('trace', this._phaseBeat + N); }
        break;
      }
      case 'drop': {
        const u = b - this._dropFrom;
        const back = DROP_BACK_BARS * BEATS_PER_BAR;
        for (let j = 0; j < N; j++) {
          const k = order[j], p = parts[k];
          let amt, spin;
          if (u < 1) { amt = DROP_OUT * smooth(u); spin = Math.PI * 2 * smooth(u); }
          else { const r = clamp01((u - 1 - j * back / N) / (back / N)); amt = DROP_OUT * (1 - smooth(r)); spin = Math.PI * 2; }
          const v = (p.side || 0) * Math.max(p.dist, 0.25) * amt;
          const sp = p.kind === 'shaft' || p.kind === 'carter' ? 0 : spin * this._spinDir[k];
          if (v !== this._dx[k] || sp !== this._spin[k]) { this._dx[k] = v; this._spin[k] = sp; this._dirty = true; }
        }
        this._itemStage = { kind: 'all', t: 0, v: 1 - clamp01(u / 0.3) };
        if (u >= 1 + back) { this._spin.fill(0); this._dx.fill(0); this._phase = 'rotate'; this._phrasesLeft = 1; this._retraceFrom = b; this._dirty = true; }
        break;
      }
    }
  }

  // --- classification: the strokes of every part in every view ------------------------------
  _classify() {
    const px = this._px;
    const pose = { yaw: this._yaw, dx: this._dx, spin: this._spin };
    const wob = (WOBBLE_PX + WOBBLE_HIGH * (this._highBand * 2 - 1)) * px;
    for (let vi = 0; vi < this._views.length; vi++) {
      const v = this._views[vi];
      const res = classifyView(this._asm, v.basis, pose, { hidden: this._layout.hidden, pxPerUnit: v.scale / px });
      v.res = res;
      const drawings = [];
      for (let k = 0; k < res.strokes.length; k++) {
        let segs = [];
        let n = 0;
        for (const s of res.strokes[k]) {
          const x0 = v.cx + s.x0 * v.scale, y0 = v.cy + s.y0 * v.scale, x1 = v.cx + s.x1 * v.scale, y1 = v.cy + s.y1 * v.scale;
          if (s.hidden) dashLine(segs, x0, y0, x1, y1, 1, DASH_PX * px, GAP_PX * px);
          else wobbleLine(segs, x0, y0, x1, y1, 0, wob * (hashf(k * 997 + n) * 2 - 1), OVERSHOOT_PX * px);
          n++;
        }
        // a small view's content never leaves its frame
        const clip = v.box || v.clip;
        if (clip) segs = segs.map((sg) => clipSeg(sg, clip)).filter(Boolean);
        drawings.push(makeDrawing(segs));
      }
      v.drawings = drawings;
    }
    this._buildAnnotations();
  }

  // project an object point in a view (with the part's pose)
  _proj(v, x, y, z, k) {
    const M = v.res.M;
    const x2 = x + (k != null ? this._dx[k] : 0);
    return [v.cx + v.scale * (M[0] * x2 + M[1] * y + M[2] * z), v.cy + v.scale * (M[3] * x2 + M[4] * y + M[5] * z)];
  }

  // The cotes and annotations as ITEMS, each drawn in one beat of COTE: the
  // length cotes (in drawing order), the diameters, the balloons, the cut
  // A-A. A cote is two drawings: its lines, then its value - the value is
  // what flashes in accent on the downbeats of ROTATE, as a checked figure.
  _buildAnnotations() {
    const v = this._views[0], px = this._px;
    const parts = this._asm.parts;
    const font = this._font || getHersheySans();
    const TXT = 15 * px;
    const bbox = this._viewBBox(v);
    const items = [];
    let lowest = bbox[1];
    this._diaTop = -1e9;
    if (this._layout.cotes) {
      const rowY = -0.66, row2 = -0.86;
      for (const k of this._asm.order) {
        const p = parts[k];
        const y = p.kind === 'shaft' ? row2 : rowY;
        const segs = [], val = [];
        const a = this._proj(v, p.x0, y, 0), bb = this._proj(v, p.x1, y, 0);
        const value = (p.len * SCALE_MM).toFixed(1);
        // a value that does not fit between its arrows is not written: on a
        // real sheet it would go to a detail view
        const room = Math.hypot(bb[0] - a[0], bb[1] - a[1]);
        if (room < textWidth(font, value) * TXT + 10 * px) continue;
        const ea = this._proj(v, p.x0, -Math.min(p.r, 0.4) - 0.03, 0), eb = this._proj(v, p.x1, -Math.min(p.r, 0.4) - 0.03, 0);
        const ea2 = this._proj(v, p.x0, y - 0.05, 0), eb2 = this._proj(v, p.x1, y - 0.05, 0);
        segs.push([ea[0], ea[1], ea2[0], ea2[1], 2], [eb[0], eb[1], eb2[0], eb2[1], 2], [a[0], a[1], bb[0], bb[1], 2]);
        arrowHead(segs, a[0], a[1], bb[0], bb[1], 8 * px);
        arrowHead(segs, bb[0], bb[1], a[0], a[1], 8 * px);
        textSegs(val, font, value, (a[0] + bb[0]) / 2, (a[1] + bb[1]) / 2 + 5 * px, TXT, 0.5, 2);
        for (const sg of segs.concat(val)) lowest = Math.min(lowest, sg[1], sg[3]);
        items.push({ kind: 'length', lines: makeDrawing(segs), value: makeDrawing(val) });
      }
      // diameters: the three largest round parts, stacked so that no two labels meet
      const round = parts.map((p, k) => k).filter((k) => parts[k].dia && parts[k].kind !== 'shaft' && parts[k].kind !== 'spring')
        .sort((a, b) => parts[b].dia - parts[a].dia).slice(0, 3)
        .sort((a, b) => this._proj(v, (parts[a].x0 + parts[a].x1) / 2, parts[a].r, 0)[0] - this._proj(v, (parts[b].x0 + parts[b].x1) / 2, parts[b].r, 0)[0]);
      let slot = 0;
      for (const k of round) {
        const p = parts[k];
        const segs = [], val = [];
        const rim = this._proj(v, (p.x0 + p.x1) / 2, p.r, 0);
        const elbow = [rim[0] + 16 * px, Math.max(rim[1] + 24 * px, bbox[3] + (10 + 22 * slot) * px)];
        this._diaTop = Math.max(this._diaTop, elbow[1] + TXT * 1.3);
        slot++;
        const label = (p.dia * SCALE_MM).toFixed(1);
        const w = textWidth(font, label) * TXT + 1.1 * TXT;
        segs.push([rim[0], rim[1], elbow[0], elbow[1], 2], [elbow[0], elbow[1], elbow[0] + w, elbow[1], 2]);
        diameterSign(segs, elbow[0] + 0.45 * TXT, elbow[1] + 0.55 * TXT, 0.4 * TXT);
        textSegs(val, font, label, elbow[0] + 1.0 * TXT, elbow[1] + 4 * px, TXT, 0, 2);
        items.push({ kind: 'dia', lines: makeDrawing(segs), value: makeDrawing(val) });
      }
    }
    if (this._layout.balloons) {
      const bs = [];
      const idx = parts.map((p, i) => i).sort((a, b2) => this._proj(v, (parts[a].x0 + parts[a].x1) / 2, 0, 0)[0] - this._proj(v, (parts[b2].x0 + parts[b2].x1) / 2, 0, 0)[0]);
      const R = 11 * px;
      let lastX = -1e9;
      for (const k of idx) {
        const p = parts[k];
        const c = this._proj(v, (p.x0 + p.x1) / 2, Math.min(p.r, 0.35) * 0.6, 0);
        const bx = Math.max(c[0], lastX + 2.6 * R);
        lastX = bx;
        const by = Math.max(bbox[3] + 34 * px, this._diaTop + 16 * px);
        const dx = bx - c[0], dy = by - R - c[1], dl = Math.hypot(dx, dy) || 1;
        bs.push([c[0], c[1], bx - dx / dl * R, by - dy / dl * R, 2]);
        circleSegs(bs, bx, by, R, 18, 2);
        textSegs(bs, font, String(p.repere), bx, by - 0.45 * 0.8 * R * 1.3, R * 1.1, 0.5, 2);
      }
      items.push({ kind: 'balloons', lines: makeDrawing(bs), value: null });
    }
    if (this._layout.section) {
      // the cut A-A through the housing: a chain line, heavy ends, arrows, letters, its title
      const ss = [];
      const c = this._proj(v, 0, 0, 0);
      const top = bbox[3] + 14 * px, bot = lowest - 16 * px;
      mixedLine(ss, c[0], bot, c[0], top, 2, px);
      ss.push([c[0], top, c[0], top - 12 * px, 0], [c[0], bot, c[0], bot + 12 * px, 0]);
      for (const yy of [top, bot]) {
        ss.push([c[0], yy, c[0] - 22 * px, yy, 2]);
        arrowHead(ss, c[0], yy, c[0] - 22 * px, yy, 8 * px);
        textSegs(ss, font, 'A', c[0] - 34 * px, yy - 7 * px, TXT, 0.5, 2);
      }
      textSegs(ss, font, 'SECTION A-A', c[0], bot - 26 * px, TXT, 0.5, 2);
      items.push({ kind: 'section', lines: makeDrawing(ss), value: null });
    }
    this._items = items;
    // the axis in chain line, for the explosion
    const ax = [];
    const x0 = Math.min(...parts.map((p, k) => p.x0 + this._dx[k])) - 0.12, x1 = Math.max(...parts.map((p, k) => p.x1 + this._dx[k])) + 0.12;
    const a = this._proj(v, x0, 0, 0), bb = this._proj(v, x1, 0, 0);
    mixedLine(ax, a[0], a[1], bb[0], bb[1], 2, px);
    this._axisD = makeDrawing(ax);
    this._statics = this._buildStatics(font);
  }

  _viewBBox(v) {
    const b = v.res.bbox;
    return [v.cx + b[0] * v.scale, v.cy + b[1] * v.scale, v.cx + b[2] * v.scale, v.cy + b[3] * v.scale];
  }

  // The sheet: the small views' frames and titles, the general notes, the
  // title block, the ruler. All the lettering is English.
  _buildStatics(font) {
    const px = this._px, HH = this._HH, HW = this._HW;
    const segs = [];
    const m = 0.05 * HH;
    for (const v of this._views) {
      if (!v.box) continue;
      const [x0, y0, x1, y1] = v.box;
      rectSegs(segs, x0, y0, x1, y1, 2);
      textSegs(segs, font, v.label, x0 + 8 * px, y0 + 6 * px, 11 * px, 0, 2);
    }
    // the general notes, bottom left, small
    const ry = -HH + 0.6 * m;
    const nH = 9 * px;
    NOTES.forEach((line, i) => textSegs(segs, font, line, -HW + m, ry + (18 + 14 * (NOTES.length - 1 - i)) * px, nH, 0, 2));
    let rulerRight = HW - m;
    this._cartouche = null;
    if (this._layout.cartouche) {
      // its lower edge 12% of the frame's height above the bottom: clear of the UI's controls
      const W = Math.min(0.98 * HH, 0.32 * 2 * HW), H = 0.46 * HH;
      const x1 = HW - m, x0 = x1 - W, y0 = -HH + CARTOUCHE_LIFT * 2 * HH, y1 = y0 + H;
      rectSegs(segs, x0, y0, x1, y1, 0);
      const yT = y1 - 0.30 * H, yA = yT - 0.20 * H, yR = yA - 0.25 * H;
      segs.push([x0, yT, x1, yT, 2], [x0, yA, x1, yA, 2], [x0, yR, x1, yR, 2]);
      const c1 = x0 + 0.27 * W, c2 = x0 + 0.62 * W, c3 = x0 + 0.34 * W;
      segs.push([c1, yR, c1, yA, 2], [c2, yR, c2, yA, 2], [c3, y0, c3, yR, 2]);
      const pad = 7 * px;
      const title = cleanText(this._title) || 'UNTITLED';
      const artist = cleanText(this._artist) || '-';
      const d = new Date();
      const date = 'DATE ' + d.getFullYear() + '-' + String(d.getMonth() + 1).padStart(2, '0') + '-' + String(d.getDate()).padStart(2, '0');
      const cell = (str, cx0, cx1, cy0, cy1, maxH, weight) => {
        const h = Math.min(maxH, (cy1 - cy0) * 0.5, (cx1 - cx0 - 2 * pad) / Math.max(0.5, textWidth(font, str)));
        textSegs(segs, font, str, cx0 + pad, cy0 + (cy1 - cy0 - h) / 2, h, 0, weight);
      };
      cell(title, x0, x1, yT, y1, 1e9, 1);
      cell(artist, x0, x1, yA, yT, 1e9, 2);
      cell('SCALE 1:2', x0, c1, yR, yA, 13 * px, 2);
      cell('DWG NO. ' + this._plan, c1, c2, yR, yA, 13 * px, 2);
      cell(date, c2, x1, yR, yA, 13 * px, 2);
      cell('SHEET 1 OF 1', x0, c3, y0, yR, 13 * px, 2);
      cell('DRAWN BY ' + APP_NAME, c3, x1, y0, yR, 13 * px, 2);
      rulerRight = x0 - m;
      this._cartouche = { x0, y0, x1, y1, title, artist, date, plan: this._plan };
    }
    // the ruler along the bottom edge, stopping before the title block
    const rx0 = -HW + m;
    segs.push([rx0, ry, rulerRight, ry, 2]);
    const step = 8 * px;
    for (let i = 0, x = rx0; x <= rulerRight + 1e-9; i++, x += step) {
      const h = i % 10 === 0 ? 11 : i % 5 === 0 ? 7 : 4;
      segs.push([x, ry, x, ry + h * px, 2]);
    }
    this._rulerRight = rulerRight;
    return makeDrawing(segs);
  }

  // The cut plane of ROTATE's downbeat, u = 0..1 across the assembly in one
  // beat: its chain line, and every part it cuts hatched at 45 degrees in
  // accent (the section of an extrusion at x is its profile at x).
  _sweepDrawing(u) {
    const v = this._views[0], px = this._px, parts = this._asm.parts;
    const font = this._font || getHersheySans();
    let xmin = 1e9, xmax = -1e9, ry = 0;
    for (const p of parts) { xmin = Math.min(xmin, p.x0); xmax = Math.max(xmax, p.x1); ry = Math.max(ry, p.r); }
    const xs = lerp(xmin - 0.04, xmax + 0.04, u);
    const segs = [];
    const a = this._proj(v, xs, -ry - 0.14, 0), b = this._proj(v, xs, ry + 0.14, 0);
    mixedLine(segs, a[0], a[1], b[0], b[1], 1, px);
    textSegs(segs, font, 'A', a[0], a[1] - 18 * px, 13 * px, 0.5, 2);
    textSegs(segs, font, 'A', b[0], b[1] + 6 * px, 13 * px, 0.5, 2);
    if (this._layout.section) {
      for (let k = 0; k < parts.length; k++) {
        for (const m of parts[k].meshes) {
          const n = (m.V - 2) / 2;
          const mx0 = Math.min(m.pos[0], m.pos[n * 3]), mx1 = Math.max(m.pos[0], m.pos[n * 3]);
          if (xs < mx0 || xs > mx1) continue;
          const poly = [];
          for (let i = 0; i < n; i++) poly.push(this._proj(v, xs, m.pos[i * 3 + 1], m.pos[i * 3 + 2], k));
          for (let i = 0; i < n; i++) segs.push([poly[i][0], poly[i][1], poly[(i + 1) % n][0], poly[(i + 1) % n][1], 1]);
          hatchPolygon(segs, poly, SECTION_HATCH_PX * px, 2);
        }
      }
    }
    return makeDrawing(segs);
  }

  // --- emission: the drawings at their progress, into the three buffers --------------
  _emit(b) {
    for (const L of this._lines) L.n = 0;
    this._penN = 0;
    const flicker = this._dropLead && (b % 1) < 0.5;
    this._draw(this._statics, clamp01((b - this._staticFrom) / 2), false);
    // the parts, in every view; in ROTATE one of them is inspected in accent for the beat
    const inspecting = this._phase === 'rotate' && Math.floor(b) === this._inspectBeat;
    for (const v of this._views) {
      if (!v.drawings) continue;
      for (let k = 0; k < v.drawings.length; k++) this._draw(v.drawings[k], this._partP[k], inspecting && k === this._inspect ? true : null);
    }
    // the cotes and annotations; the values flash in accent on ROTATE's downbeats
    const st = this._itemStage;
    const sinceBar = b - this._lastBarBeat;
    const flash = this._phase === 'rotate' && sinceBar >= 0 && sinceBar < 0.5;
    this._items.forEach((it, i) => {
      const p = st.kind === 'seq' ? clamp01((st.t - i) / TRACE_OF_BEAT) : st.kind === 'all' ? st.v : 0;
      if (p <= 0) return;
      const pl = it.value ? clamp01(p / 0.6) : p, pv = clamp01((p - 0.6) / 0.4);
      this._draw(it.lines, pl, it.kind === 'section' && flicker ? true : null);
      if (it.value) this._draw(it.value, pv, flash && pv >= 1 ? true : null);
    });
    // the cut plane's sweep
    if (this._phase === 'rotate' && this._sweepFrom != null) {
      const u = b - this._sweepFrom;
      if (u >= 0 && u < 1) this._draw(this._sweepDrawing(u), 1, true);
    }
    if (this._axisD) this._draw(this._axisD, this._axisP, false);
    for (const L of this._lines) { L.geo.instanceCount = L.n; L.pos.needsUpdate = true; L.col.needsUpdate = true; }
    this._pens.geometry.setDrawRange(0, this._penN);
    this._pens.geometry.attributes.position.needsUpdate = true;
  }

  // One drawing at progress p. accentMode null: accent while it is being drawn,
  // white once done; true / false: forced.
  _draw(d, p, accentMode) {
    if (!d || p <= 0 || !d.total) return;
    const active = accentMode === null ? (p > 0 && p < 1) : accentMode;
    const col = active ? this._accent : this._ink;
    const reach = p * d.total;
    let acc = 0;
    for (let i = 0; i < d.segs.length; i++) {
      const s = d.segs[i];
      if (acc >= reach) break;
      let x1 = s[2], y1 = s[3];
      if (acc + s[5] > reach) {
        const f = (reach - acc) / s[5];
        x1 = s[0] + (s[2] - s[0]) * f; y1 = s[1] + (s[3] - s[1]) * f;
        if (p < 1) this._pen(x1, y1);
      }
      this._push(s[4], s[0], s[1], x1, y1, col);
      acc += s[5];
    }
  }

  _push(w, x0, y0, x1, y1, c) {
    const L = this._lines[w];
    if (L.n >= L.cap) return;
    const a = this._safeAt((x0 + x1) / 2, (y0 + y1) / 2);
    const P = L.pos.array, C = L.col.array, o = L.n * 6;
    P[o] = x0; P[o + 1] = y0; P[o + 2] = 0.01; P[o + 3] = x1; P[o + 4] = y1; P[o + 5] = 0.01;
    C[o] = c[0] * a; C[o + 1] = c[1] * a; C[o + 2] = c[2] * a; C[o + 3] = c[0] * a; C[o + 4] = c[1] * a; C[o + 5] = c[2] * a;
    L.n++;
  }
  _pen(x, y) {
    if (this._penN >= 8) return;
    this._penPos[this._penN * 3] = x; this._penPos[this._penN * 3 + 1] = y; this._penPos[this._penN * 3 + 2] = 0.02;
    this._penN++;
  }
  _safeAt(x, y) {
    const c = safeZoneUniforms.uSafeCenter.value, h = safeZoneUniforms.uSafeHalfSize.value;
    const feather = safeZoneUniforms.uSafeFeather.value;
    const sx = x / this._HH, sy = y / this._HH;
    const qx = Math.abs(sx - c.x) - h.x, qy = Math.abs(sy - c.y) - h.y;
    const d = Math.hypot(Math.max(qx, 0), Math.max(qy, 0)) + Math.min(Math.max(qx, qy), 0);
    return lerp(SAFE_FLOOR, 1, smooth(d / feather));
  }

  // --- for the tests -----------------------------------------------------------------
  get state() {
    return {
      phase: this._phase, phaseBeat: this._phaseBeat, beat: this._beatClock, parts: this._asm.parts.length,
      partP: Array.from(this._partP), items: this._items.map((it) => it.kind), itemStage: Object.assign({}, this._itemStage),
      yaw: this._yaw, yawTo: this._yawTo, dx: Array.from(this._dx), order: this._asm.order.slice(), cartouche: this._cartouche,
      inspect: this._inspect, inspectBeat: this._inspectBeat, sweepFrom: this._sweepFrom, lastBarBeat: this._lastBarBeat
    };
  }
  get buffers() { return this._lines.map((L) => ({ n: L.n, pos: L.pos.array, col: L.col.array })); }
  get pens() { return { n: this._penN, pos: this._penPos }; }
  get colours() { return { ink: this._ink, accent: this._accent, accentDisplay: this._accentDisplay }; }
  get views() { return this._views; }

  dispose() {
    if (!this._scene) return;
    for (const L of this._lines) { this._scene.remove(L.mesh); L.geo.dispose(); L.mat.dispose(); }
    this._scene.remove(this._paper); this._paper.geometry.dispose(); this._paper.material.dispose();
    this._scene.remove(this._pens); this._pens.geometry.dispose(); this._pens.material.dispose();
    this._scene = null;
  }
}

// =============================================================================
// 2D drawing helpers: strokes are [x0, y0, x1, y1, weight, length]
// =============================================================================

function makeDrawing(list) {
  const segs = [];
  let total = 0;
  for (const s of list) { const l = Math.hypot(s[2] - s[0], s[3] - s[1]); if (l <= 0) continue; segs.push([s[0], s[1], s[2], s[3], s[4], l]); total += l; }
  return { segs, total };
}
function wobbleLine(out, x0, y0, x1, y1, w, bend, over) {
  const dx = x1 - x0, dy = y1 - y0, l = Math.hypot(dx, dy) || 1;
  const ux = dx / l, uy = dy / l;
  const ax = x0 - ux * over, ay = y0 - uy * over, bx = x1 + ux * over, by = y1 + uy * over;
  const mx = (ax + bx) / 2 - uy * bend, my = (ay + by) / 2 + ux * bend;
  out.push([ax, ay, mx, my, w], [mx, my, bx, by, w]);
}
function dashLine(out, x0, y0, x1, y1, w, dash, gap) {
  const dx = x1 - x0, dy = y1 - y0, l = Math.hypot(dx, dy);
  if (l <= 0) return;
  for (let s = 0; s < l; s += dash + gap) {
    const e = Math.min(l, s + dash);
    out.push([x0 + dx * s / l, y0 + dy * s / l, x0 + dx * e / l, y0 + dy * e / l, w]);
  }
}
function mixedLine(out, x0, y0, x1, y1, w, px) {
  const dx = x1 - x0, dy = y1 - y0, l = Math.hypot(dx, dy);
  if (l <= 0) return;
  const pat = MIXED.map((v) => v * px);
  let s = 0, i = 0;
  while (s < l) {
    const e = Math.min(l, s + pat[i % 4]);
    if (i % 2 === 0) out.push([x0 + dx * s / l, y0 + dy * s / l, x0 + dx * e / l, y0 + dy * e / l, w]);
    s = e; i++;
  }
}
function arrowHead(out, x, y, fromX, fromY, size) {
  // an arrow AT (x, y), pointing away from (fromX, fromY)... i.e. into the extension line
  const dx = x - fromX, dy = y - fromY, l = Math.hypot(dx, dy) || 1;
  const ux = dx / l, uy = dy / l;
  const a = 0.35;
  for (const s of [-1, 1]) {
    const ca = Math.cos(a * s), sa = Math.sin(a * s);
    const rx = -(ux * ca - uy * sa), ry = -(ux * sa + uy * ca);
    out.push([x, y, x + rx * size, y + ry * size, 2]);
  }
}
function circleSegs(out, cx, cy, r, n, w) {
  for (let i = 0; i < n; i++) {
    const a0 = i / n * Math.PI * 2, a1 = (i + 1) / n * Math.PI * 2;
    out.push([cx + r * Math.cos(a0), cy + r * Math.sin(a0), cx + r * Math.cos(a1), cy + r * Math.sin(a1), w]);
  }
}
function rectSegs(out, x0, y0, x1, y1, w) { out.push([x0, y0, x1, y0, w], [x1, y0, x1, y1, w], [x1, y1, x0, y1, w], [x0, y1, x0, y0, w]); }
function diameterSign(out, cx, cy, r) { circleSegs(out, cx, cy, r, 12, 2); out.push([cx - r * 1.25, cy - r * 1.25, cx + r * 1.25, cy + r * 1.25, 2]); }
function hatchPolygon(out, poly, spacing, w) {
  // 45-degree lines x - y = c, clipped to the polygon by the even-odd rule:
  // the crossings are sorted and taken in pairs, so a toothed section (not
  // convex) is hatched on the metal only
  let cmin = 1e9, cmax = -1e9;
  for (const p of poly) { const c = p[0] - p[1]; cmin = Math.min(cmin, c); cmax = Math.max(cmax, c); }
  const s2 = spacing * Math.SQRT2;
  for (let c = Math.ceil(cmin / s2) * s2; c <= cmax; c += s2) {
    const hits = [];
    for (let i = 0; i < poly.length; i++) {
      const a = poly[i], b = poly[(i + 1) % poly.length];
      const fa = a[0] - a[1] - c, fb = b[0] - b[1] - c;
      if ((fa <= 0 && fb > 0) || (fa > 0 && fb <= 0)) { const t = fa / (fa - fb); hits.push([a[0] + (b[0] - a[0]) * t, a[1] + (b[1] - a[1]) * t]); }
    }
    hits.sort((p, q) => p[0] - q[0]);
    for (let k = 0; k + 1 < hits.length; k += 2) out.push([hits[k][0], hits[k][1], hits[k + 1][0], hits[k + 1][1], w]);
  }
}
// Liang-Barsky: a stroke clipped to a rectangle, or null
function clipSeg(sg, box) {
  const [x0, y0, x1, y1] = sg;
  const dx = x1 - x0, dy = y1 - y0;
  let t0 = 0, t1 = 1;
  const p = [-dx, dx, -dy, dy], q = [x0 - box[0], box[2] - x0, y0 - box[1], box[3] - y0];
  for (let i = 0; i < 4; i++) {
    if (p[i] === 0) { if (q[i] < 0) return null; continue; }
    const r = q[i] / p[i];
    if (p[i] < 0) { if (r > t1) return null; if (r > t0) t0 = r; }
    else { if (r < t0) return null; if (r < t1) t1 = r; }
  }
  return [x0 + dx * t0, y0 + dy * t0, x0 + dx * t1, y0 + dy * t1, sg[4]];
}
function cleanText(s) { return (s || '').replace(/\s+/g, ' ').trim().toUpperCase().slice(0, 60); }
const TRACK = 0.12;
function textWidth(font, str) {
  if (!font) return 0;
  let w = 0;
  for (const ch of str) { const base = ch.normalize('NFD')[0]; w += (font.advances[base] != null ? font.advances[base] : 0.7) + TRACK; }
  return Math.max(0, w - TRACK);
}
// Hershey single-stroke text; accents the font lacks are drawn by hand
function textSegs(out, font, str, x, y, h, align, w) {
  if (!font) return;
  const width = textWidth(font, str) * h;
  let cx = x - width * align;
  for (const ch of str) {
    const nfd = ch.normalize('NFD');
    const base = nfd[0];
    const g = font.glyphs[base] || font.glyphs['?'];
    const adv = font.advances[base] != null ? font.advances[base] : 0.7;
    if (g) for (const pl of g) for (let i = 0; i + 1 < pl.length; i++) out.push([cx + pl[i][0] * h, y + pl[i][1] * h, cx + pl[i + 1][0] * h, y + pl[i + 1][1] * h, w]);
    const mid = cx + adv * h * 0.5, top = y + (base === base.toUpperCase() ? 1.12 : 0.82) * h;
    for (const mark of nfd.slice(1)) {
      if (mark === '́') out.push([mid - 0.08 * h, top, mid + 0.12 * h, top + 0.18 * h, w]);
      else if (mark === '̀') out.push([mid + 0.08 * h, top, mid - 0.12 * h, top + 0.18 * h, w]);
      else if (mark === '̂') out.push([mid - 0.14 * h, top, mid, top + 0.16 * h, w], [mid, top + 0.16 * h, mid + 0.14 * h, top, w]);
      else if (mark === '̈') out.push([mid - 0.12 * h, top, mid - 0.12 * h, top + 0.06 * h, w], [mid + 0.12 * h, top, mid + 0.12 * h, top + 0.06 * h, w]);
    }
    cx += (adv + TRACK) * h;
  }
}

const _size = new THREE.Vector2();
